'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { products } from '@/data/products';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function ProductDetailsPage() {
  const params = useParams();
  const id = Number(params?.id);
  const product = products?.find((p) => p?.id === id) || products?.[0];

  const [timeLeft, setTimeLeft] = useState(15 * 60 + 25);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((t) => t > 0 ? t - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const minutes = Math.floor(timeLeft / 60)?.toString()?.padStart(2, '0');
  const seconds = (timeLeft % 60)?.toString()?.padStart(2, '0');

  return (
    <div>
      <Navbar />
      <div className="pt-14 md:pt-16">
        {/* Flash Deal Banner */}
        <div className="bg-[#0c831f] text-white text-xs font-bold text-center py-2.5 tracking-widest uppercase">
          ⚡ FLASH COMBO DEAL • EXTRA SAVINGS ACTIVE • LIMITED TIME ONLY
        </div>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2 text-xs text-gray-500">
          <Link href="/" className="hover:text-[#0c831f] transition-colors">Home</Link>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span className="hover:text-[#0c831f] cursor-pointer transition-colors">Combo Offers</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span className="text-gray-800 font-medium line-clamp-1">{product?.name}</span>
        </div>

        {/* Product Section */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Left: Image */}
            <div className="flex flex-col gap-4">
              <div className="relative bg-white rounded-2xl border border-gray-200 overflow-hidden p-4">
                <div className="relative">
                  <img
                    alt={product?.name}
                    src={product?.image}
                    className="w-full object-contain rounded-xl" />
                </div>
                <span className="absolute top-4 left-4 bg-red-500 text-white text-sm font-extrabold px-3 py-1 rounded-lg shadow">
                  {product?.discount}% Off
                </span>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0c831f]">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span className="font-semibold text-[#0c831f]">100% Genuine</span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                <p className="text-xs text-gray-500">Trusted by 1M+ customers • Safe &amp; Secure Payments</p>
              </div>
            </div>

            {/* Right: Details */}
            <div className="flex flex-col gap-5">
              <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-snug">
                {product?.name}
              </h1>

              {/* Rating & Bought */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-yellow-400 fill-yellow-400">
                    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                  </svg>
                  <span className="text-sm font-bold text-gray-800">4.5 ★</span>
                  <span className="text-gray-400">&bull;</span>
                  <span className="text-sm text-gray-600">{product?.reviews}+ ratings</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0c831f]">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  <span className="font-bold text-[#0c831f]">128 bought today</span>
                </div>
              </div>

              {/* Price */}
              <div className="border-t border-b border-gray-100 py-4">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-3xl md:text-4xl font-extrabold text-gray-900">₹{product?.price}</span>
                  <span className="text-lg text-gray-400 line-through">₹{product?.originalPrice}</span>
                  <span className="bg-red-500 text-white text-sm font-extrabold px-2.5 py-1 rounded-lg">{product?.discount}% Off</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">(Inclusive of all taxes)</p>
              </div>

              {/* Countdown */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-orange-500">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="text-sm text-orange-700">
                  Offer ends in <span className="font-extrabold text-orange-800">{minutes}:{seconds}</span>
                </span>
              </div>

              {/* Stock */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-600">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="text-sm text-yellow-800">
                  Hurry! Only <span className="font-extrabold">7 units</span> left in stock
                </span>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-3">
                {[
                { icon: '🚚', text: 'Delivery in 4 hours' },
                { icon: '🔄', text: '7-Day Easy Returns' },
                { icon: '✅', text: '100% Genuine Product' },
                { icon: '🔒', text: 'Secure Checkout' }]?.
                map((feat) =>
                <div key={feat?.text} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                    <span className="text-base">{feat?.icon}</span>
                    <span className="text-xs font-semibold text-gray-700">{feat?.text}</span>
                  </div>
                )}
              </div>

              {/* Buy Now Button */}
              <Link
                href={`/shopping-cart/${product?.id}`}
                className="block">
                
                <button className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-extrabold text-base transition-all active:scale-95 shadow-lg bg-[#0c831f] text-white hover:bg-[#065f12]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="8" cy="21" r="1" />
                    <circle cx="19" cy="21" r="1" />
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                  </svg>
                  BUY NOW • SAVE MORE
                </button>
              </Link>

              {/* Payment Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-1">
                <p className="text-xs text-gray-500 text-center">💳 Pay via UPI, Cards, Net Banking, Wallets | 🔒 SSL Encrypted</p>
                <p className="text-xs text-gray-400 text-center">🔒 We do not store card details • 100% secure payment</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>);

}